import React, { useMemo, useRef, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Divider,
} from "@mui/material";
import { instantiateDesignFromModel } from "../components/editor/modelUtils";
import { listMyModels } from "../services/modelService";
import { useLocation, useNavigate } from "react-router-dom";
import { Context } from "../App";
import { createDesign, uploadDesignPreview } from "../services/designService";
import { uploadMedia } from "../services/mediaService";
import Header from "../components/layout/Header";
import PublicationPanel from "../components/editor/panels/PublicationPanel";
import ModelMiniature from "../components/models/ModelMiniature";
import ImageFieldInput from "../components/forms/ImageFieldInput";
import { captureDesignToBlob } from "../utils/captureDesign";

export default function CreateDesignFromModel() {
    const location = useLocation();
    const navigate = useNavigate();
    const { deconnect } = React.useContext(Context);
    const preselectedModelId = location.state?.modelId || "";

    const [models, setModels] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [values, setValues] = useState({});
    const [duration, setDuration] = useState("");
    const [publishTo, setPublishTo] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // fieldName → File (pending upload)
    const pendingFilesRef = useRef(new Map());
    // fieldName → blob URL (for live preview)
    const previewUrlsRef = useRef(new Map());

    // Revoke blob URLs on unmount
    useEffect(() => {
        const previews = previewUrlsRef.current;
        return () => { previews.forEach((url) => URL.revokeObjectURL(url)); };
    }, []);

    React.useEffect(() => {
        let active = true;
        const run = async () => {
            try {
                const data = await listMyModels();
                if (!active) return;
                setModels(data);
                if (preselectedModelId) setSelectedId(preselectedModelId);
            } catch (e) {
                if (!active) return;
                if (e.message === "SESSION_EXPIRED") {
                    alert("Votre session a expiré. Veuillez vous reconnecter.");
                    deconnect();
                    return;
                }
                setError("Erreur lors du chargement des modèles.");
            } finally {
                if (active) setLoading(false);
            }
        };
        run();
        return () => { active = false; };
    }, [deconnect, preselectedModelId]);

    const selectedModel = useMemo(
        () => models.find((m) => m._id === selectedId) || null,
        [models, selectedId]
    );

    const fields = useMemo(
        () => (selectedModel?.modelFields || []).filter((f) => !f.fixed),
        [selectedModel]
    );

    // Live preview — recomputes on every field value change
    const liveDesign = useMemo(() => {
        if (!selectedModel) return null;
        return instantiateDesignFromModel(selectedModel, values);
    }, [selectedModel, values]);

    const handleSave = async () => {
        if (!selectedModel) return;
        if (!title.trim()) {
            alert("Le titre est requis.");
            return;
        }
        setSaving(true);
        try {
            // Upload pending image files and resolve final URL values
            const resolvedValues = { ...values };
            const pending = Array.from(pendingFilesRef.current.entries());
            if (pending.length > 0) {
                await Promise.all(pending.map(async ([fieldName, file]) => {
                    const result = await uploadMedia(file);
                    if (!result?.url) throw new Error("URL media manquante après upload");
                    resolvedValues[fieldName] = result.url;
                }));
            }

            // Re-instantiate with resolved URLs
            const finalDesign = instantiateDesignFromModel(selectedModel, resolvedValues);

            const saved = await createDesign(
                {
                    title: title.trim(),
                    description,
                    elements: finalDesign.elements,
                    canvasBg: finalDesign.canvasBg,
                    canvasSize: finalDesign.canvasSize,
                    duration: duration === "" ? undefined : Number(duration),
                    publishTo,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                },
                selectedModel._id
            );

            // Fire-and-forget: capture and upload the preview image
            captureDesignToBlob(finalDesign)
                .then((blob) => blob && uploadDesignPreview(saved._id, blob))
                .catch((err) => console.warn("[preview] Capture failed:", err.message));

            alert("Design enregistré.");
            navigate("/design");
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            alert("Erreur lors de l'enregistrement : " + (e.message || "erreur inconnue"));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                </Box>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: { md: "100vh" }, overflow: { md: "hidden" } }}>
            <Header />

            <Box sx={{ display: "flex", flex: 1, overflow: { md: "hidden" }, flexDirection: { xs: "column", md: "row" } }}>
                {/* ── Left: scrollable form ── */}
                <Box sx={{ width: { xs: "100%", md: 380 }, flexShrink: 0, overflowY: "auto", p: 2.5, borderRight: { md: "1px solid" }, borderBottom: { xs: "1px solid", md: "none" }, borderColor: "divider" }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Nouveau design
                    </Typography>

                    <Stack spacing={2}>
                        {/* Infos générales */}
                        <Typography variant="subtitle2" color="text.secondary">Général</Typography>

                        <TextField
                            size="small"
                            fullWidth
                            label="Titre *"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            label="Description"
                            multiline
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />

                        <Divider />

                        {/* Modèle */}
                        <Typography variant="subtitle2" color="text.secondary">Modèle source</Typography>

                        <FormControl fullWidth size="small">
                            <InputLabel>Modèle</InputLabel>
                            <Select
                                label="Modèle"
                                value={selectedId}
                                onChange={(e) => {
                                    setSelectedId(e.target.value);
                                    setValues({});
                                }}
                            >
                                {models.map((m) => (
                                    <MenuItem key={m._id} value={m._id}>{m.title}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedModel && fields.length > 0 && (
                            <>
                                <Typography variant="subtitle2" color="text.secondary">Données du modèle</Typography>
                                {fields.map((field) => {
                                    if (field.type === "image") {
                                        const pendingFile = pendingFilesRef.current.get(field.name) || null;
                                        const previewUrl = previewUrlsRef.current.get(field.name) || null;
                                        const urlValue = pendingFile ? "" : (values[field.name] || "");
                                        return (
                                            <ImageFieldInput
                                                key={field.name}
                                                fieldName={field.name}
                                                urlValue={urlValue}
                                                pendingFile={pendingFile}
                                                previewUrl={previewUrl}
                                                onUrlChange={(url) => {
                                                    // Clear any pending file for this field
                                                    const prev = previewUrlsRef.current.get(field.name);
                                                    if (prev) URL.revokeObjectURL(prev);
                                                    previewUrlsRef.current.delete(field.name);
                                                    pendingFilesRef.current.delete(field.name);
                                                    setValues((p) => ({ ...p, [field.name]: url }));
                                                }}
                                                onFileChange={(file, blobUrl) => {
                                                    // Revoke previous blob URL if any
                                                    const prev = previewUrlsRef.current.get(field.name);
                                                    if (prev) URL.revokeObjectURL(prev);
                                                    previewUrlsRef.current.set(field.name, blobUrl);
                                                    pendingFilesRef.current.set(field.name, file);
                                                    // Use blob URL for live preview; clear URL text
                                                    setValues((p) => ({ ...p, [field.name]: blobUrl }));
                                                }}
                                                onClearFile={() => {
                                                    const prev = previewUrlsRef.current.get(field.name);
                                                    if (prev) URL.revokeObjectURL(prev);
                                                    previewUrlsRef.current.delete(field.name);
                                                    pendingFilesRef.current.delete(field.name);
                                                    setValues((p) => ({ ...p, [field.name]: "" }));
                                                }}
                                            />
                                        );
                                    }
                                    return (
                                        <TextField
                                            key={field.name}
                                            size="small"
                                            fullWidth
                                            label={field.name}
                                            placeholder="Texte"
                                            value={values[field.name] || ""}
                                            onChange={(e) => setValues((p) => ({ ...p, [field.name]: e.target.value }))}
                                        />
                                    );
                                })}
                            </>
                        )}

                        {selectedModel && fields.length === 0 && (
                            <Typography variant="body2" color="warning.main">
                                Ce modèle ne contient pas de champs variables.
                            </Typography>
                        )}

                        <Divider />

                        {/* Diffusion */}
                        <Typography variant="subtitle2" color="text.secondary">Diffusion</Typography>
                        <PublicationPanel
                            duration={duration}
                            setDuration={setDuration}
                            publishTo={publishTo}
                            setPublishTo={setPublishTo}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                        />

                        <Divider />

                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={!selectedModel || !title.trim() || saving}
                            onClick={handleSave}
                        >
                            {saving ? "Enregistrement..." : "Enregistrer"}
                        </Button>

                        <Button variant="text" size="small" onClick={() => navigate("/design")}>
                            Annuler
                        </Button>
                    </Stack>
                </Box>

                {/* ── Right: live preview ── */}
                <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, bgcolor: "#eef0f3", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: { xs: 360, md: 0 } }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Aperçu en direct
                    </Typography>

                    {liveDesign ? (
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                            <ModelMiniature model={liveDesign} contained />
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="body2" color="text.disabled">
                                Sélectionnez un modèle pour voir l'aperçu.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
